/**
 * The data behind screen 02: /today for day 0, /week for this Monday and the
 * next for days 1-6 (the week endpoint takes Mondays only, calendar.py). No
 * server change. Every query refreshes each minute, like Today always has, and
 * on window focus (main.tsx). The ['week', monday] key is the Calendar's, so
 * the invalidations after a booking, a schedule or a time-off change cover it.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../../core/api';
import type { DayView, TodayView } from '../../types';
import { followingDays, weekMondays } from './model';

export const REFRESH_MS = 60_000;

export interface SevenDays {
  today: TodayView | undefined;
  /** Days 1-6; undefined where that week has not loaded yet. */
  following: (DayView | undefined)[];
  error: unknown;
}

export function useSevenDays(): SevenDays {
  const today = useQuery({ queryKey: ['today'], queryFn: api.today, refetchInterval: REFRESH_MS });
  const date = today.data?.date;
  const [thisMonday, nextMonday] = date ? weekMondays(date) : ['', ''];
  const thisWeek = useQuery({
    queryKey: ['week', thisMonday],
    queryFn: () => api.week(thisMonday),
    enabled: Boolean(date),
    refetchInterval: REFRESH_MS,
  });
  const nextWeek = useQuery({
    queryKey: ['week', nextMonday],
    queryFn: () => api.week(nextMonday),
    enabled: Boolean(date),
    refetchInterval: REFRESH_MS,
  });
  return {
    today: today.data,
    following: date ? followingDays(date, [thisWeek.data, nextWeek.data]) : [],
    error: today.error ?? thisWeek.error ?? nextWeek.error,
  };
}
